import axios from 'axios'
import { displayNotification } from '../../actions/notificationsActions'
import {
  putApiRequest,
  postApiRequest,
  deleteApiRequest,
} from '../../helpers/apiHelper'
import { getApiErrorMessage } from '../../helpers/apiErrorHelper'

export const createProductPackage = (params) => (dispatch) => {
  if (!params?.user_id) {
    dispatch({ type: 'CREATE_PRODUCTPACKAGE_FAILURE' })
    dispatch(
      displayNotification(
        'Cannot create product package — no customer is available. Please create customer for this user and try again.',
        'error'
      )
    )
    return Promise.reject(new Error('user_id is required'))
  }

  return postApiRequest('products/packages', params)
    .then((response) => {
      const productPackage = response.data
      dispatch({
        type: 'CREATE_PRODUCTPACKAGE',
        entity: productPackage,
      })
      dispatch({
        type: 'SET_REFI_APPLICATION_DATA',
        payload: {
          productPackageId: productPackage.id,
        },
      })
    })
    .catch((error) => {
      const status = error.response?.status
      // cheap way to check if this is a VIN required error.
      // relying on an error message in the response might be better.
      // the Coverage Details section won't pass vehicle_id so this works
      if (status === 503 && params.vehicle_id) {
        dispatch({ type: 'VIN_REQUIRED_ERROR' })
        dispatch({
          type: 'SET_APPLICATION_STATE',
          payload: {
            showVehicle: true,
            showProfile: false,
            showUnderwriting: false,
            showCoverage: false,
            showCredit: false,
            creditSectionFinished: false,
          },
        })
        dispatch(
          displayNotification(
            getApiErrorMessage(
              error,
              'An error occurred while creating the product package'
            ),
            'error'
          )
        )
      } else if (status == 422) {
        // Carry the server's reason so the VIN field can show it. Not every 422
        // on this endpoint means "no products" -- the duplicate-VIN refusal and
        // the verify-mileage copy arrive here too, and the field used to paint
        // "No products available for this vehicle" over all of them.
        dispatch({
          type: 'PRODUCTS_NOT_AVAILABLE_ERROR',
          payload: { message: getApiErrorMessage(error, '') },
        })
        dispatch({
          type: 'SET_APPLICATION_STATE',
          payload: {
            showVehicle: true,
            showProfile: false,
            showUnderwriting: false,
            showCoverage: false,
            showCredit: false,
            creditSectionFinished: false,
          },
        })
        dispatch(
          displayNotification(
            getApiErrorMessage(
              error,
              'An error occurred while creating the product package'
            ),
            'error'
          )
        )
      } else {
        const errorMessage = getApiErrorMessage(error, 'Something went wrong')
        dispatch({ type: 'CREATE_PRODUCTPACKAGE_FAILURE' })
        dispatch(displayNotification(errorMessage, 'error'))
      }
      throw error
    })
}

// Move a monthly membership package to a configured margin position. Position 0
// is the price the agent is first shown; each later position is a discount step.
// The server reprices the option and returns the updated package, so the card,
// down payment and payment options all follow from the same response.
export const applyMonthlyMembershipPosition =
  (productPackageId, position) => (dispatch) => {
    return putApiRequest(
      `products/packages/${productPackageId}/monthly_membership_position`,
      { position }
    )
      .then((response) => {
        dispatch({
          type: 'UPDATE_PRODUCTPACKAGE_SUCCESS',
          entity: response.data,
        })

        return Promise.resolve(response.data)
      })
      .catch((error) => {
        const message =
          error.response?.data?.message || 'Could not change the discount'
        dispatch(displayNotification(`${message}`, 'error'))
        throw new Error(error)
      })
  }

// Move a Florida package to a configured markup tier. Position 0 is the
// organization's default Florida markup; each later position is a fixed lower
// markup. Florida files its rates, so the agent steps between fixed tiers
// rather than discounting by an arbitrary amount.
export const applyFloridaMarkupPosition =
  (productPackageId, position) => (dispatch) => {
    return putApiRequest(
      `products/packages/${productPackageId}/florida_markup_position`,
      { position }
    )
      .then((response) => {
        dispatch({
          type: 'UPDATE_PRODUCTPACKAGE_SUCCESS',
          entity: response.data,
        })

        return Promise.resolve(response.data)
      })
      .catch((error) => {
        const message =
          error.response?.data?.message || 'Could not change the discount'
        dispatch(displayNotification(`${message}`, 'error'))
        throw new Error(error)
      })
  }

export const updateProductPackage = (productPackage) => (dispatch) => {
  return putApiRequest(`products/packages/${productPackage.id}`, productPackage)
    .then((response) => {
      const productPackage = response.data
      dispatch({
        type: 'UPDATE_PRODUCTPACKAGE_SUCCESS',
        entity: productPackage,
      })

      return Promise.resolve(productPackage)
    })
    .catch((error) => {
      const errorMessage = getApiErrorMessage(error, 'Something went wrong')
      dispatch({
        type: 'UPDATE_PRODUCTPACKAGE_FAILURE',
      })
      dispatch(displayNotification(`${errorMessage}`, 'error'))
      throw new Error(error)
    })
}

export const finalizeProductPackage = (productPackage) => (dispatch) => {
  return putApiRequest(
    `products/packages/${productPackage.id}/finalize`,
    productPackage
  )
    .then((response) => {
      const { url } = response.data || {}
      dispatch({
        type: 'FINALIZE_PRODUCTPACKAGE_SUCCESS',
        url,
      })
    })
    .catch((error) => {
      // A 422 from the server carries the reason (e.g. address_required), and
      // not always under `message`; see getApiErrorMessage.
      const errorMessage = getApiErrorMessage(error, 'Something went wrong')
      dispatch({
        type: 'FINALIZE_PRODUCTPACKAGE_FAILURE',
      })
      dispatch(displayNotification(`${errorMessage}`, 'error'))
      throw new Error(error)
    })
}

export const sendProductPackageConfirmations =
  (productPackage) => (dispatch) => {
    return putApiRequest(
      `products/packages/${productPackage.id}/send_confirmations`,
      productPackage
    )
      .then(() => {
        dispatch({
          type: 'SEND_PRODUCTPACKAGE_CONFIRMATIONS_SUCCESS',
        })
      })
      .catch((error) => {
        const errorMessage =
          error.response && error.response.data
            ? error.response.data.message
            : 'Something went wrong'
        dispatch({
          type: 'SEND_PRODUCTPACKAGE_CONFIRMATIONS_FAILURE',
        })
        dispatch(displayNotification(`${errorMessage}`, 'error'))
        throw new Error(error)
      })
  }

export const cleanProductPackageData =
  (productPackageId: number) => (dispatch) => {
    return deleteApiRequest(
      `products/packages/${productPackageId}/clean_data`
    ).catch((error) => {
      const errorMessage =
        error.response && error.response.data
          ? error.response.data.error
          : 'Something went wrong'
      dispatch(displayNotification(`${errorMessage}`, 'error'))
      throw new Error(error)
    })
  }

export const optInProductPackage =
  ({ vehicle_id, user_id }) =>
  (dispatch) => {
    return putApiRequest(`products/packages/${vehicle_id}/opt_in`, { user_id })
      .then(() =>
        dispatch({
          type: 'OPT_IN_PRODUCTPACKAGE_SUCCESS',
        })
      )
      .catch((error) => {
        const errorMessage =
          error.response && error.response.data
            ? error.response.data.message
            : 'Something went wrong'
        dispatch({
          type: 'OPT_IN_PRODUCTPACKAGE_FAILURE',
        })
        dispatch(displayNotification(`${errorMessage}`, 'error'))
        throw new Error(error)
      })
  }
export const FETCH_PRODUCT_PACKAGE_PAYMENT_OPTIONS =
    'FETCH_PRODUCT_PACKAGE_PAYMENT_OPTIONS',
  FETCH_PRODUCT_PACKAGE_PAYMENT_OPTIONS_SUCCESS =
    'FETCH_PRODUCT_PACKAGE_PAYMENT_OPTIONS_SUCCESS',
  FETCH_PRODUCT_PACKAGE_PAYMENT_OPTIONS_ERROR =
    'FETCH_PRODUCT_PACKAGE_PAYMENT_OPTIONS_ERROR',
  RESET_PRODUCT_PACKAGE_PAYMENT_OPTIONS =
    'RESET_PRODUCT_PACKAGE_PAYMENT_OPTIONS'

export const calculatePaymentOptions =
  (downPayment, productPackageId, userId, paymentPlanProvider) =>
  (dispatch) => {
    dispatch({ type: FETCH_PRODUCT_PACKAGE_PAYMENT_OPTIONS })
    axios
      .get(
        `${process.env.REACT_APP_BLINKER_API_URL}/api/v3/admin/products/packages/${productPackageId}/payment_options`,
        {
          params: {
            user_id: userId,
            down_payment: downPayment,
            payment_plan_provider: paymentPlanProvider,
          },
        }
      )
      .then((res) => {
        const treatedData = res.data.map((option) => ({
          numberPayment: option.payment_count,
          monthValue: option.value,
          total: option.total,
        }))
        // The backend stored the provider with this request. Mirror it onto the
        // package entity before the options land: PaymentDetails remounts when
        // they do and seeds its select from the entity, so a stale provider
        // there would be shown and, on the next Next, written back over this
        // one. UPDATE_..._SUCCESS rather than CREATE_...: both merge into byId,
        // but CREATE appends the id to allIds again.
        dispatch({
          type: 'UPDATE_PRODUCTPACKAGE_SUCCESS',
          entity: { id: productPackageId, paymentPlanProviderSource: paymentPlanProvider },
        })
        dispatch({
          type: FETCH_PRODUCT_PACKAGE_PAYMENT_OPTIONS_SUCCESS,
          payload: treatedData,
        })
      })
      .catch((error) => {
        if (error.response) {
          const message = error.response.data?.error
          if (message) {
            dispatch(
              displayNotification(
                `Error Getting Payment Options - ${message}`,
                'error'
              )
            )
            setTimeout(() => {
              window.location.href = `users/${userId}`
            }, 3000)
          } else {
            dispatch(
              displayNotification(
                `Error Getting Payment Options - ${error.message}`,
                'error'
              )
            )
          }
        }
        dispatch({
          type: FETCH_PRODUCT_PACKAGE_PAYMENT_OPTIONS_ERROR,
        })
      })
  }
